import { MutableRefObject, useCallback, useState } from 'react';

import type { BaseRowData, LegacyDataProvider } from '@adv-frontend/data-provider';
import { HStack, VStack } from '@direct-frontend/components';

import cn from 'classnames';
import type { GridRowSelectionManager } from '../../../lib/selectionManager/selectionManager';
import type { GridTable, GridHeader, GridHeaderGroup } from '../../../types';
import { HeaderCell } from '../HeaderCell/HeaderCell';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `HeaderGroups`.
 *
 * @typeParam RowData Данные грида.
 */
type HeaderGroupsProps<RowData extends BaseRowData> = {
    /**
     * CSS-класс, который будет добавлен к контейнеру группы заголовков грида.
     */
    className?: string;
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Ссылка на scrollable элемент грида.
     */
    gridRef: MutableRefObject<HTMLDivElement | null>;
    /**
     * Ссылка на глобальный ресайзер грида.
     */
    commonResizerRef: MutableRefObject<HTMLDivElement | null>;
    /**
     * Смещение для группы колонок.
     */
    groupLeftOffset?: number;
    /**
     * Инстанс группы заголовков.
     */
    headerGroups: GridHeaderGroup<RowData>[];
    /**
     * Инстансы подзаголовков.
     */
    subHeaders: GridHeader<RowData>[];
    /**
     * Объект с признаками видимости ресайзеров для заголовков.
     */
    visibleResizers?: Record<string, boolean>;
    /**
     * Датапровайдер.
     */
    dataProvider: LegacyDataProvider<RowData>;
    /**
     * Менеджер выделения строк.
     */
    selectionManager: GridRowSelectionManager | undefined;
    /**
     * Скрывать ли нижний бордер у шапки.
     */
    hideHeaderBorderBottom: boolean;
};

/**
 * Компонент списка групп заголовков грида.
 *
 * @typeParam RowData Данные грида.
 */
export function HeaderGroups<RowData extends BaseRowData>({
    className,
    headerGroups,
    grid,
    gridRef,
    groupLeftOffset,
    commonResizerRef,
    subHeaders,
    visibleResizers,
    dataProvider,
    selectionManager,
    hideHeaderBorderBottom,
}: HeaderGroupsProps<RowData>) {
    const hasSubHeader = grid.getLeafHeaders().some(header => Boolean(header.column.columnDef.renderSubHeader));

    // Сохраняем высоты всех строк в шапке (строка с группировкой, строка заголовков),
    // чтобы иметь возможность правильно вычислить высоту ресайзера
    const [headersHeights, setHeadersHeights] = useState<number[]>([]);
    const setHeadersHeightByIndex = useCallback((element: HTMLElement | null, index: number) => {
        if (!element) {
            return;
        }

        setHeadersHeights(currentValue => {
            if (element.clientHeight === currentValue[index]) {
                return currentValue;
            }

            const nextValue = [...currentValue];
            nextValue[index] = element.clientHeight;

            return nextValue;
        });
    }, []);

    return (
        <VStack className={className}>
            {headerGroups.map((headerGroup, index) => {
                // Высота ресайзера должна исключать высоту всех строк шапки, до текущей
                const rowTopOffset = headersHeights.slice(0, index).reduce((sum, height) => sum + height, 0);

                return (
                    <HStack
                        className={cn(styles.headerGroups, hideHeaderBorderBottom && styles.hideHeaderBorderBottom)}
                        key={headerGroup.id}
                        data-testid={index === headerGroups.length - 1 ? 'Grid.ColumnHeaders' : undefined}
                        // Используем стрелочную функцию, чтобы пробросить параметры в setHeadersHeightByIndex
                        // Новая ссылка на функцию-ref не приводит к ререндеру и позволят сильно упростить код
                        // https://react.dev/reference/react-dom/components/common#ref-callback
                        // eslint-disable-next-line react/jsx-no-bind -- См. выше
                        ref={element => setHeadersHeightByIndex(element, index)}
                    >
                        {headerGroup.headers.map(header => (
                            <HeaderCell
                                commonResizerRef={commonResizerRef}
                                dataProvider={dataProvider}
                                selectionManager={selectionManager}
                                grid={grid}
                                gridRef={gridRef}
                                groupLeftOffset={groupLeftOffset}
                                header={header}
                                key={`header-${header.id}`}
                                rowTopOffset={rowTopOffset}
                                isVisibleResizer={visibleResizers ? Boolean(visibleResizers[header.id]) : true}
                                placeholderHeight={headersHeights[index]}
                            />
                        ))}
                    </HStack>
                );
            })}

            {hasSubHeader && (
                <HStack
                    className={cn(
                        styles.subHeaderGroup,
                        !subHeaders.length ? styles.subHeaderGroupEmpty : undefined,
                        hideHeaderBorderBottom && styles.hideHeaderBorderBottom
                    )}
                >
                    {subHeaders.map(header => (
                        <HeaderCell
                            key={`subheader-${header.id}`}
                            header={header}
                            grid={grid}
                            isSubHeader
                            dataProvider={dataProvider}
                            selectionManager={selectionManager}
                        />
                    ))}
                </HStack>
            )}
        </VStack>
    );
}
